// Hook that tracks the prefers-reduced-motion media query. Returns true when
// the user has requested reduced motion. Reads the current state synchronously
// on mount (no flash) then subscribes for live changes.
//
// Safe in SSR contexts: when window or matchMedia is unavailable the hook
// returns false (the "animation allowed" default) without throwing.

import { useEffect, useState } from "react";

export function usePrefersReducedMotion(): boolean {
  // Lazy initialiser reads the current media-query state synchronously on
  // mount. The effect only sets up the subscription; setState inside the
  // subscription callback is the allowed pattern.
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = () => setReduced(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return reduced;
}
